import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CircularProgress from '@material-ui/core/CircularProgress';

import { userSignInSuccess } from 'actions/Auth';
import LogoHorizontal from 'assets/images/logo-horizontal.svg';
import FormField from 'components/atoms/FormFields/FormField';
import AppLogoContent from 'components/organisms/AppLogoContent';
import TwoFaRequired from 'components/organisms/Modal/TwoFaRequired';
import Modal from 'components/organisms/Modal';
import CustomToast from 'components/organisms/Modal/CustomToast';
import TwoFaValidator from 'components/organisms/Modal/TwoFaValidator';
import { enableTwoFaWithSpecialToken, signIn } from 'services/api';
import IntlMessages from 'util/IntlMessages';
import { SIGN_IN_SCHEMA } from 'util/yupSchemas';
import { clearTrustedDeviceToken, getTrustedDeviceToken, setTrustedDeviceToken } from 'util/trustedDevice';

function SignIn() {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { from } = location.state || { from: { pathname: '/app/dashboard' } };
  const [isFetching, setIsFetching] = useState(false);
  const [showTwoFaValidator, setShowTwoFaValidator] = useState(false);
  const [showExpiredSession, setShowExpiredSession] = useState(false);
  const [request2Fa, setRequest2Fa] = useState(false);
  const [userData, setUserData] = useState({});
  const [twoFaData, setTwoFaData] = useState({});
  const [hasCredentialsError, setHasCredentialsError] = useState(false);
  const { formState, handleSubmit, register, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(SIGN_IN_SCHEMA),
  });

  const signInErrorAlert = (messageId, className) => {
    toast.error(<IntlMessages id={messageId} defaultMessage={<IntlMessages id="error.signIn.unspecific" />} />, {
      id: 'login-error-message',
      position: 'top-center',
      className,
    });
  };

  // Disabled/locked accounts intentionally show the same generic credentials message as any
  // other login failure — the app never reveals account-lock state to the login form.
  const signInErrorHandler = (errorSlug) => {
    switch (errorSlug) {
      case 'user-has-2fa':
        setShowTwoFaValidator(true);
        break;
      case 'invalid-2fa':
        setShowTwoFaValidator(true);
        signInErrorAlert(`error.signIn.${errorSlug}`);
        break;
      default:
        setHasCredentialsError(true);
        signInErrorAlert('error.signIn.invalid-credentials', 'app-login--toast-credentials');
    }
  };

  const handleSignIn = async (formValues) => {
    const trustedDeviceToken = getTrustedDeviceToken(formValues.email);
    try {
      setIsFetching(true);
      setHasCredentialsError(false);
      showTwoFaValidator && setShowTwoFaValidator(false);
      const useTrustedDevice = trustedDeviceToken && !formValues.code_2fa;
      const payload = useTrustedDevice ? { ...formValues, trusted_device_token: trustedDeviceToken } : formValues;
      const user = await signIn(payload);
      if (user?.data?.require_2fa) {
        handleRequired2Fa(user.data);
      } else {
        user?.data?.trusted_device_token && setTrustedDeviceToken(formValues.email, user.data.trusted_device_token);
        setSignInData(user);
      }
    } catch (err) {
      const errorSlug = err?.response?.data?.message;
      errorSlug === 'user-has-2fa' && trustedDeviceToken && clearTrustedDeviceToken(formValues.email);
      signInErrorHandler(errorSlug);
    } finally {
      setIsFetching(false);
    }
  };

  const setSignInData = (user) => {
    localStorage.removeItem('alertDisplayed');
    localStorage.setItem('user_data', JSON.stringify(user.data));
    dispatch(userSignInSuccess(user.data));
    history.push(from);
  };

  const handleRequired2Fa = (user) => {
    if (!user.has_2fa) {
      setUserData(user);
      setTwoFaData({ secret: user?.secret, qrCode: user?.qr_url });
      setRequest2Fa(true);
    }
  };

  const activeTwoFa = async (code) => {
    try {
      setIsFetching(true);
      const res = await enableTwoFaWithSpecialToken({ token_2fa: userData?.token_2fa, code_2fa: code });
      setRequest2Fa(false);
      toast.success(<IntlMessages id="component.passwordMananger.2fa.successMessage" />, {
        id: 'success-message',
      });
      setSignInData({ data: { ...res.data, has_2fa: true } });
    } catch (err) {
      toast.error(<IntlMessages id="error.2faSetup.unspecific" />, {
        id: '2fa-setup-unspecific',
        position: 'top-center',
      });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (JSON.parse(localStorage.getItem('show_expired_session'))) {
      setShowExpiredSession(true);
      localStorage.setItem('show_expired_session', false);
    }

    return () => {
      setShowExpiredSession(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-login--container">
      <CustomToast isOpen={showExpiredSession} close={() => setShowExpiredSession(false)} type="sessionExpired" />
      <div className="app-login--wrapper">
        <AppLogoContent />

        <div className="app-login--content">
          <div className="app-login--form-wrapper">
            <form onSubmit={handleSubmit(handleSignIn)}>
              <div className="app-login--logo-wrapper">
                <h1>
                  <img src={LogoHorizontal} alt="cripten" />
                </h1>
                <span className="text-cripten-primary">Flash</span>
              </div>
              <div className="input-container">
                <FormField
                  name="email"
                  type="email"
                  errors={formState.errors}
                  size="full-size"
                  register={register}
                  placeholder={intl.formatMessage({
                    id: 'app.login.form.user',
                  })}
                />
                <FormField
                  name="password"
                  type="password"
                  errors={formState.errors}
                  register={register}
                  size="full-size"
                  placeholder={intl.formatMessage({
                    id: 'app.login.form.password',
                  })}
                />
                <Link to="/forgot-password">
                  <IntlMessages id="app.login.forgotPassword" />
                </Link>
                {hasCredentialsError && (
                  <div className="app-login--inline-error text-red">
                    <IntlMessages id="error.signIn.invalid-credentials" />
                  </div>
                )}
              </div>
              <div className="app-login--btn-wrapper">
                <button type="submit" disabled={isFetching} className="app-login--submit-btn">
                  <IntlMessages id="appModule.enter" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {isFetching && (
        <div className="loader-view">
          <CircularProgress />
        </div>
      )}
      <Modal
        isOpen={showTwoFaValidator}
        closeAction={() => {
          setShowTwoFaValidator(false);
        }}
      >
        <TwoFaValidator
          handleClick={handleSubmit(handleSignIn)}
          handleChange={setValue}
          fieldName="code_2fa"
          trustFieldName="trust_device"
        />
      </Modal>
      <Modal
        isOpen={request2Fa}
        closeAction={() => {
          setRequest2Fa(false);
        }}
      >
        <TwoFaRequired
          handleClose={() => setRequest2Fa(false)}
          twoFaData={twoFaData}
          onSubmit={activeTwoFa}
          isFetching={isFetching}
        />
      </Modal>
    </div>
  );
}

export default SignIn;
